import React, { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as AlertIcon } from '../../../../../../assets/icons/alert-color-icon-ne.svg';
import CardNE from '../../../../../../shared/components/CardNE/CardNE';
import DropzoneFileUploader from '../../../../../../shared/components/DropzoneFileUploader/DropzoneFileUploader';
import StyledButtonNE from '../../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { EXCEL_CONTENT_TYPE } from '../../../../../../shared/constants/constants';
import { DOWNLOAD_IMAGES } from '../../../../../../shared/constants/images';
import {
  ERROR_FILE_NAME,
  POINTS_FILE_VALIDATION_ERRORS,
  REWARDS_POINTS_UPLOADER_VALIDATORS
} from '../../../../../../shared/constants/rewards';
import {
  CLICK_ACTION,
  POINTS_DOWNLOAD_COLLABORATORS_FILE_ERRORS,
  POINTS_DOWNLOAD_COLLABORATORS_FILE_ERRORS_AND_CONTINUE,
  POINTS_DOWNLOAD_COLLABORATORS_FILE_LATER
} from '../../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { IRewardsFileValidation } from '../../../../../../shared/interfaces/rewards';
import Rewards from '../../../../../../shared/services/api/rewards/rewards';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../../shared/translations/commons';
import { TRANSLATION_POINTS_BY_FILE } from '../../../../../../shared/translations/rewards';
import { base64ToFile } from '../../../../../../shared/utils/utils';
import FileErrorModal from '../../FileErrorModal/FileErrorModal';

interface ReminderCardProps {
  setValidatedData: (pointsInfo: IRewardsFileValidation | undefined) => void;
  setFileInfo: (base64file: string | undefined) => void;
  previousPointsInfo: IRewardsFileValidation;
}

const ReminderCard = ({ setValidatedData, setFileInfo, previousPointsInfo }: ReminderCardProps) => {
  const [file, setFile] = useState<string>('');
  const [fileError, setFileError] = useState<string>();
  const [pointsInfo, setPointsInfo] = useState<IRewardsFileValidation>();
  const [loadingValidation, setLoadingValidation] = useState<boolean>(false);
  const [openPartialModal, setOpenPartialModal] = useState<boolean>(false);
  const [resetFile, setResetFile] = useState<boolean>(false);
  const [emptyValidation, setEmptyValidation] = useState<boolean>(false);
  const [showCorrectedFileMessage, setShowCorrectedFileMessage] = useState<boolean>(false);

  const { t } = useTranslation();
  const { company } = useContext(CompanyContext);

  const downloadErrorFile = (rewardsPointsInfo: IRewardsFileValidation | undefined) => {
    if (rewardsPointsInfo?.errors) {
      base64ToFile(rewardsPointsInfo?.errors, EXCEL_CONTENT_TYPE, ERROR_FILE_NAME);
      TagManagerService.pushEvent(CLICK_ACTION, POINTS_DOWNLOAD_COLLABORATORS_FILE_ERRORS);
    }
  };

  const updateBase64 = (fileBase64: string) => {
    if (fileBase64) {
      setLoadingValidation(true);
      uploadFile(fileBase64);
      setFile(fileBase64);
      setFileError(undefined);
    } else {
      deleteFile();
    }
  };

  const deleteFile = () => {
    setFile('');
    setFileError(undefined);
    setValidatedData(undefined);
    setFileInfo(undefined);
  };

  const uploadFile = async (fileBase64: string) => {
    try {
      const response = await Rewards.validatePointsFile(company.id, fileBase64);
      setPointsInfo(response);
      if (response.valid) {
        setValidatedData(response);
        setFileInfo(fileBase64);
      } else if (!response.validsCount && response.errors) {
        setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.noValidData));
        setEmptyValidation(true);
      } else {
        setOpenPartialModal(true);
      }
    } catch (error: any) {
      setShowCorrectedFileMessage(true);
      switch (error?.response?.data?.code) {
        case POINTS_FILE_VALIDATION_ERRORS.MAX_FILE_SIZE:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.maxRows));
          break;
        case POINTS_FILE_VALIDATION_ERRORS.EMPTY_FILE_ERROR:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.emptyFile));
          break;
        case POINTS_FILE_VALIDATION_ERRORS.INVALID_FORMAT:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.format));
          break;
        default:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.default));
          break;
      }
    } finally {
      setLoadingValidation(false);
    }
  };

  const modalPrimaryButtonAction = () => {
    setValidatedData(pointsInfo);
    setFileInfo(file);
    setOpenPartialModal(false);
    downloadErrorFile(pointsInfo);
    TagManagerService.pushEvent(CLICK_ACTION, POINTS_DOWNLOAD_COLLABORATORS_FILE_ERRORS_AND_CONTINUE);
  };

  const modalSecondaryButtonAction = () => {
    setResetFile(!resetFile);
    deleteFile();
    setOpenPartialModal(false);
    TagManagerService.pushEvent(CLICK_ACTION, POINTS_DOWNLOAD_COLLABORATORS_FILE_LATER);
  };

  const handleCloseModal = (_: Record<string, unknown>, reason: string) => {
    if (reason && reason === 'backdropClick') {
      return true;
    }
  };

  useEffect(() => {
    setResetFile(!resetFile);
    deleteFile();
  }, [previousPointsInfo]);

  return (
    <CardNE className="justify-between gap-4">
      <AlertIcon height={32} width={32} />
      <div className="flex flex-row w-full justify-between items-center">
        <div className="flex flex-col w-72 gap-2">
          <h3 className="ne-font-heading-md m-0">
            {t(TRANSLATION_POINTS_BY_FILE.successWithErrors.reminderCard.title, {
              rows: previousPointsInfo.errorsCount,
              count: previousPointsInfo.errorsCount
            })}
          </h3>
          <p className="ne-font-body-md m-0">
            {t(TRANSLATION_POINTS_BY_FILE.successWithErrors.reminderCard.description)}
          </p>
        </div>

        <StyledButtonNE
          buttonType="secondary"
          buttonSize="sm"
          classes="w-auto"
          imageLeft={DOWNLOAD_IMAGES}
          action={() => downloadErrorFile(previousPointsInfo)}
        >
          {t(TRANSLATION_POINTS_BY_FILE.successWithErrors.reminderCard.downloadErrors)}
        </StyledButtonNE>
      </div>
      <div className="flex flex-row w-full justify-between items-center gap-4">
        <DropzoneFileUploader
          externalError={fileError}
          updateBase64={(base64File) => updateBase64(base64File)}
          reset={resetFile}
          loading={loadingValidation}
          validator={REWARDS_POINTS_UPLOADER_VALIDATORS}
          inputTitle={t(TRANSLATION_POINTS_BY_FILE.successWithErrors.reminderCard.fileUploader)}
          correctedFileMessage={showCorrectedFileMessage}
          ActionButton={
            emptyValidation ? (
              <StyledButtonNE
                buttonType="secondary"
                action={() => downloadErrorFile(pointsInfo)}
                imageLeft={DOWNLOAD_IMAGES}
              >
                {t(TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsByFile.seeErrorsButton)}
              </StyledButtonNE>
            ) : undefined
          }
        />
      </div>
      <FileErrorModal
        isOpen={openPartialModal}
        validatedInfo={pointsInfo}
        primaryButtonAction={modalPrimaryButtonAction}
        secondaryButtonAction={modalSecondaryButtonAction}
        handleClose={handleCloseModal}
      />
    </CardNE>
  );
};

export default ReminderCard;
